"use client";

import { useMemo } from "react";
import { Button } from "@/components/ui/button";

export function getPageNumbers(
 currentPage: number,
 totalPages: number,
): Array<number | "ellipsis"> {
 const pages: Array<number | "ellipsis"> = [];
 const maxVisible = 7;

 if (totalPages <= maxVisible) {
  for (let i = 1; i <= totalPages; i++) {
   pages.push(i);
  }
  return pages;
 }

 pages.push(1);

 if (currentPage > 3) {
  pages.push("ellipsis");
 }

 const start = Math.max(2, currentPage - 1);
 const end = Math.min(totalPages - 1, currentPage + 1);

 for (let i = start; i <= end; i++) {
  pages.push(i);
 }

 if (currentPage < totalPages - 2) {
  pages.push("ellipsis");
 }

 pages.push(totalPages);

 return pages;
}

interface ListingPaginationProps {
 page: number;
 totalPages: number;
 isLoading: boolean;
 onPageChange: (page: number) => void;
}

function PageStepButton({
 label,
 disabled,
 onClick,
}: {
 label: string;
 disabled: boolean;
 onClick: () => void;
}) {
 return (
  <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onClick}>
   {label}
  </Button>
 );
}

function PageNumberButtons({
 pageNumbers,
 page,
 isLoading,
 onPageChange,
}: {
 pageNumbers: Array<number | "ellipsis">;
 page: number;
 isLoading: boolean;
 onPageChange: (page: number) => void;
}) {
 return (
  <>
   {pageNumbers.map((pageNumber, index) =>
    pageNumber === "ellipsis" ? (
     <span
      key={`ellipsis-${index}`}
      className="px-2 text-xs text-muted-foreground"
     >
      ...
     </span>
    ) : (
     <Button
      key={pageNumber}
      type="button"
      variant={pageNumber === page ? "primary" : "outline"}
      size="sm"
      disabled={isLoading}
      onClick={() => onPageChange(pageNumber)}
     >
      {pageNumber}
     </Button>
    ),
   )}
  </>
 );
}

export function ListingPagination({
 page,
 totalPages,
 isLoading,
 onPageChange,
}: ListingPaginationProps) {
 const pageNumbers = useMemo(
  () => getPageNumbers(page, totalPages),
  [page, totalPages],
 );

 if (totalPages <= 1) return null;

 return (
  <div className="border-t border-border bg-background px-4 py-3">
   <nav
    className="flex items-center justify-center gap-1"
    aria-label="Pagination"
   >
    <PageStepButton label="First" disabled={page <= 1 || isLoading} onClick={() => onPageChange(1)} />
    <PageStepButton label="Prev" disabled={page <= 1 || isLoading} onClick={() => onPageChange(page - 1)} />
    <PageNumberButtons
     pageNumbers={pageNumbers}
     page={page}
     isLoading={isLoading}
     onPageChange={onPageChange}
    />
    <PageStepButton label="Next" disabled={page >= totalPages || isLoading} onClick={() => onPageChange(page + 1)} />
    <PageStepButton label="Last" disabled={page >= totalPages || isLoading} onClick={() => onPageChange(totalPages)} />
   </nav>
  </div>
 );
}
